import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid2';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SortableHeaderCell from '../../common/table/sortable-header-cell';
import RowActionsMenu from '../../common/table/row-actions-menu';
import TableResultsSummary from '../../common/table/table-results-summary';
import {
  FilterControl,
  FilterSelect,
  FilterTextInput,
} from '../../common/table/filter-controls';
import handleEmptyTableResponse from '../../common/table/utils/handle-empty-table-response';
import {
  compareText,
  getNextSortDirection,
  getStoredFilters,
  includesNormalized,
  saveFilters,
  sortRows,
} from '../../../../utils/tableUtils';
import getTranslationLanguageLabel from '../../../../utils/translation-language-label';
import LanguageFilter from '../../common/language-filter/language-filter';

const FILTERS_STORAGE_KEY = 'admin-translation-user-filters';
const INITIAL_FILTERS = {
  name: '',
  role: '',
  languages: [],
};

const SORT_FIELDS = {
  NAME: 'name',
  LAST_NAME: 'last_name',
  EMAIL: 'email',
  ROLE: 'role',
};

const SORT_COMPARATORS = {
  [SORT_FIELDS.NAME]: (firstUser, secondUser) => compareText(firstUser.name, secondUser.name),
  [SORT_FIELDS.LAST_NAME]: (firstUser, secondUser) => (
    compareText(firstUser.last_name, secondUser.last_name)
  ),
  [SORT_FIELDS.EMAIL]: (firstUser, secondUser) => compareText(firstUser.email, secondUser.email),
  [SORT_FIELDS.ROLE]: (firstUser, secondUser) => compareText(firstUser.role, secondUser.role),
};

const ROLE_OPTIONS = [
  { value: 'translator', label: 'Translator' },
  { value: 'reviewer', label: 'Reviewer' },
];

const formatAssignedLanguage = (languageCode, countryCode, t) => (
  getTranslationLanguageLabel(languageCode, countryCode, t)
);

const formatAssignedLanguages = (languages = [], t) => languages
  .map((assignment) => {
    const languageCode = typeof assignment === 'string' ? assignment : assignment.language;
    if (languageCode.startsWith('en')) return null;
    const countryCode = typeof assignment === 'string'
      ? ''
      : assignment.country || assignment.area_code;
    return formatAssignedLanguage(languageCode, countryCode, t);
  })
  .filter(Boolean)
  .join(', ');

const TableTranslationUsers = ({
  loadTranslationUsers,
  reload = false,
  onDataLoaded = () => {},
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [translationUsers, setTranslationUsers] = useState([]);
  const [filters, setFilters] = useState(() => (
    getStoredFilters(FILTERS_STORAGE_KEY, INITIAL_FILTERS)
  ));
  const [appliedFilters, setAppliedFilters] = useState(() => (
    getStoredFilters(FILTERS_STORAGE_KEY, INITIAL_FILTERS)
  ));
  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [loadError, setLoadError] = useState(false);
  const hasRequestedUsers = useRef(false);

  useEffect(() => {
    if (hasRequestedUsers.current && !reload) return undefined;
    hasRequestedUsers.current = true;

    let active = true;
    setDataLoaded(false);
    setLoadError(false);
    loadTranslationUsers()
      .then((users) => {
        if (!active) return;
        if (!Array.isArray(users) || users.length === 0) {
          handleEmptyTableResponse({
            clearStateSetters: [setTranslationUsers],
            onDataLoaded,
            setDataLoaded,
          });
          return;
        }
        setTranslationUsers(users);
        setDataLoaded(true);
        onDataLoaded();
      })
      .catch(() => {
        if (!active) return;
        setTranslationUsers([]);
        setLoadError(true);
        setDataLoaded(true);
        onDataLoaded();
      });

    return () => {
      active = false;
    };
  }, [loadTranslationUsers, onDataLoaded, reload]);

  const languageOptions = useMemo(() => {
    const seen = new Set();
    return translationUsers
      .flatMap((user) => (user.languages || []).map((assignment) => {
        const languageCode = typeof assignment === 'string' ? assignment : assignment.language;
        const countryCode = typeof assignment === 'string' ? '' : assignment.country || assignment.area_code;
        return { languageCode, countryCode, value: countryCode ? `${countryCode}:${languageCode}` : languageCode };
      }))
      .filter(({ languageCode, value }) => {
        if (languageCode.startsWith('en')) return false;
        if (seen.has(value)) return false;
        seen.add(value);
        return true;
      })
      .map(({ languageCode, countryCode, value }) => ({
        language: languageCode,
        country: countryCode,
        value,
        assignmentName: formatAssignedLanguage(languageCode, countryCode, t),
      }));
  }, [translationUsers, t]);

  const filteredUsers = useMemo(() => {
    const filtered = translationUsers.filter((user) => {
      const matchesName = !appliedFilters.name
        || includesNormalized(user.name, appliedFilters.name)
        || includesNormalized(user.last_name, appliedFilters.name)
        || includesNormalized(user.email, appliedFilters.name);
      const matchesRole = !appliedFilters.role || user.role === appliedFilters.role;
      const matchesLanguages = !appliedFilters.languages.length
        || (user.languages || []).some((assignment) => {
          const languageCode = typeof assignment === 'string' ? assignment : assignment.language;
          const countryCode = typeof assignment === 'string' ? '' : assignment.country || assignment.area_code;
          const value = countryCode ? `${countryCode}:${languageCode}` : languageCode;
          return appliedFilters.languages.some((filter) => filter.value === value);
        });
      return matchesName && matchesRole && matchesLanguages;
    });
    return sortRows(filtered, orderBy, order, SORT_COMPARATORS);
  }, [appliedFilters, order, orderBy, translationUsers]);

  const handleFilterChange = ({ target: { name, value } }) => {
    setFilters((currentFilters) => ({ ...currentFilters, [name]: value }));
  };

  const handleLanguageFilterChange = (_, selected) => {
    setFilters((currentFilters) => ({ ...currentFilters, languages: selected }));
  };

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    saveFilters(FILTERS_STORAGE_KEY, INITIAL_FILTERS);
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
    saveFilters(FILTERS_STORAGE_KEY, filters);
  };

  const handleRequestSort = (field) => {
    setOrder(getNextSortDirection(orderBy, order, field));
    setOrderBy(field);
  };

  if (!dataLoaded) {
    return <div>{t('common-loading')}</div>;
  }

  return (
    <>
      <div className="filters">
        <p>{t('common-search')}</p>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FilterControl
              id="translation-user-name"
              label={`${t('common-name')}/${t('common-last-name')}/${t('common-email')}`}
            >
              <FilterTextInput
                id="translation-user-name"
                name="name"
                value={filters.name}
                onChange={handleFilterChange}
              />
            </FilterControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FilterControl
              id="translation-user-role"
              label={t('translation-user-role-label', { defaultValue: 'Role' })}
            >
              <FilterSelect
                id="translation-user-role"
                name="role"
                value={filters.role}
                onChange={handleFilterChange}
                options={ROLE_OPTIONS}
                allLabel={t('translation-users-all-roles', { defaultValue: 'All roles' })}
              />
            </FilterControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 8, md: 4 }}>
            <FilterControl
              id="translation-user-languages"
              label={t('translation-user-languages-label', { defaultValue: 'Assigned languages' })}
            >
              <LanguageFilter
                options={languageOptions}
                value={filters.languages}
                getOptionLabel={(option) => option.assignmentName}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={handleLanguageFilterChange}
                placeholder={t('translation-user-languages-placeholder', { defaultValue: 'Search country or language' })}
                checkboxes
              />
            </FilterControl>
          </Grid>
        </Grid>
        <TableResultsSummary count={filteredUsers.length} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <div className="actions">
              <button className="btn" type="button" onClick={applyFilters}>
                {t('common-apply')}
              </button>
            </div>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <div className="actions">
              <button className="btn text narrow" type="button" onClick={resetFilters}>
                {t('common-reset-filters')}
              </button>
            </div>
          </Grid>
        </Grid>
      </div>

      <div className="table-wrapper">
        {filteredUsers.length > 0 ? (
          <Table aria-label={t('admin-main-tabs-tab5', { defaultValue: 'Translators' })} className="user-table">
            <TableHead>
              <TableRow>
                <SortableHeaderCell
                  field={SORT_FIELDS.NAME}
                  orderBy={orderBy}
                  order={order}
                  onRequestSort={handleRequestSort}
                  label={t('common-name')}
                />
                <SortableHeaderCell
                  field={SORT_FIELDS.LAST_NAME}
                  orderBy={orderBy}
                  order={order}
                  onRequestSort={handleRequestSort}
                  label={t('common-last-name')}
                />
                <SortableHeaderCell
                  field={SORT_FIELDS.EMAIL}
                  orderBy={orderBy}
                  order={order}
                  onRequestSort={handleRequestSort}
                  label={t('common-email')}
                />
                <SortableHeaderCell
                  field={SORT_FIELDS.ROLE}
                  orderBy={orderBy}
                  order={order}
                  onRequestSort={handleRequestSort}
                  label={t('translation-user-role-label', { defaultValue: 'Role' })}
                />
                <TableCell>
                  <span className="text">
                    {t('translation-user-languages-label', { defaultValue: 'Assigned languages' })}
                  </span>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell><span className="text">{user.name}</span></TableCell>
                  <TableCell><span className="text">{user.last_name}</span></TableCell>
                  <TableCell><span className="text">{user.email}</span></TableCell>
                  <TableCell><span className="text">{user.role}</span></TableCell>
                  <TableCell>
                    <span className="text">{formatAssignedLanguages(user.languages, t)}</span>
                  </TableCell>
                  <TableCell align="right">
                    <RowActionsMenu
                      actions={[
                        {
                          key: 'edit-translation-user',
                          label: t('translation-user-details-title'),
                          onClick: () => navigate(
                            `/admin/edit-translation-user/${user.role}/${user.id}`
                          ),
                        },
                        {
                          key: 'delete-translation-user',
                          label: t('admin-main-tab2-option3'),
                          onClick: () => navigate('/admin/delete-user', {
                            state: {
                              user,
                            },
                          }),
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="no-result">
            <h4 className="txt-center">{t('admin-main-title-empty')}</h4>
          </div>
        )}
      </div>
      <Snackbar
        open={loadError}
        autoHideDuration={3000}
        onClose={() => setLoadError(false)}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('common-internal-server-error')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TableTranslationUsers;
